<template>
  <!-- 平台结算绑定半层：通过 transition 实现从底部滑出/滑入的过渡动画 -->
  <transition name="psbs">
    <div v-if="visible" class="psbs-mask" @click.self.prevent>
      <div class="psbs-sheet" @click.stop>
        <div class="psbs-header">
          <p class="psbs-title">{{ $t("guild_platform_settlement") }}</p>
          <span class="psbs-close" @click="$emit('close')"></span>
        </div>

        <div class="psbs-scroll hide-scrollbar" @focusin="onFieldFocus" @focusout="onBodyFocusOut">
          <div class="psbs-body">
            <div class="psbs-field">
              <label>{{ $t("guild_page_country") }}<i class="req">*</i></label>
              <div class="psbs-select" @click="openSelect(1)">
                <span>{{ form.country.name }}</span>
              </div>
            </div>

            <div v-if="form.country.code === 'SA'" class="psbs-field">
              <label>{{ $t("guild_page_bank") }}<i class="req">*</i></label>
              <div class="psbs-select" @click="openSelect(2)">
                <span>{{ form.bank }}</span>
              </div>
            </div>

            <div class="psbs-field">
              <label>{{ $t("guild_page_name") }}<i class="req">*</i></label>
              <input
                v-model="form.name"
                type="text"
                :placeholder="$t('guild_please_enter')"
              />
            </div>

            <div v-if="form.country.code === 'SA'" class="psbs-field">
              <label>{{ $t("guild_page_bank_number") }}<i class="req">*</i></label>
              <input
                v-model="form.bankAccount"
                type="text"
                :placeholder="$t('guild_page_sa')"
              />
            </div>

            <div class="psbs-field">
              <label>{{ $t("guild_page_address") }}<i class="req">*</i></label>
              <input
                v-model="form.address"
                type="text"
                :placeholder="$t('guild_page_format')"
              />
            </div>

            <div class="psbs-tips">
              <p class="psbs-tips-title">{{ $t("guild_bind_notice_title") }}</p>
              <ul>
                <li>{{ $t("guild_settlement_pcoin_amount") }}</li>
                <li>{{ $t("guild_settlement_note_1") }}</li>
                <li>{{ $t("guild_settlement_note_2", { n: downNum }) }}</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="psbs-float-bar">
          <button type="button" class="psbs-submit" @click="confirmBind">
            {{ $t("guild_confirm_bind") }}
          </button>
        </div>

        <!-- 国家/银行选择层：遮罩淡入淡出，面板从底部滑出 -->
        <transition name="psbs-select-mask">
          <div v-if="selectPopup > 0" class="psbs-select-mask" @click="closeSelect"></div>
        </transition>
        <transition name="psbs-select">
          <div v-if="selectPopup > 0" class="psbs-select-popup">
            <SelectPopup
              :list="selectList"
              @changePopup="closeSelect"
              @changeCountryBank="onSelectItem"
            />
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * 平台结算渠道信息绑定半层（2026-06-08）
 * 字段与交互对齐旧 toPlatform 地址表单；遮罩点击不关闭
 */
import { Toast } from "vant";
import keepFocusedInputVisible from "@/mixins/keepFocusedInputVisible";
import SelectPopup from "@/components/withdrawal/select_pop_vue.vue";
import {
  getDefaultCountryList,
  SA_BANK_LIST,
  validateSettlementForm,
} from "@/utils/platformSettlement";

export default {
  name: "platform-settlement-bind-sheet",
  mixins: [keepFocusedInputVisible],
  keyboardAvoidance: {
    maskSelector: ".psbs-mask",
    sheetSelector: ".psbs-sheet",
    scrollSelector: ".psbs-scroll",
    margin: 16,
    bottomInset: 104,
  },
  components: { SelectPopup },
  props: {
    visible: { type: Boolean, default: false },
    downNum: { type: Number, default: 0 },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: {},
      selectPopup: 0,
      selectList: [],
      countryList: [],
      bankList: SA_BANK_LIST,
    };
  },
  watch: {
    // 半层显示时同步一次表单
    visible(val) {
      if (val) {
        this.syncForm();
      }
    },
    // 父组件传入的表单值变化时同步（深监听）
    value: {
      deep: true,
      handler() {
        if (this.visible) this.syncForm();
      },
    },
  },
  // 初始化：加载默认国家列表并同步表单
  created() {
    this.countryList = getDefaultCountryList(this.$t.bind(this));
    this.syncForm();
  },
  methods: {
    // 同步父组件传入的表单：用默认值补全缺失字段
    syncForm() {
      const defaults = {
        country: this.countryList[0],
        bank: this.bankList[0],
        name: "",
        bankAccount: "",
        address: "",
      };
      // 深拷贝父组件 value，避免直接修改外部对象
      this.form = { ...defaults, ...JSON.parse(JSON.stringify(this.value || {})) };
      // 国家/银行为空时回退到默认值
      if (!this.form.country) this.form.country = this.countryList[0];
      if (!this.form.bank) this.form.bank = this.bankList[0];
    },
    // 打开选择器：type=1 国家列表，type=2 银行列表
    openSelect(type) {
      this.dismissKeyboard();
      this.selectPopup = type;
      if (type === 1) {
        this.selectList = this.countryList.map((item) => item.name);
      } else {
        this.selectList = this.bankList;
      }
    },
    // 关闭选择器
    closeSelect() {
      this.selectPopup = 0;
    },
    // 选择国家/银行后的联动处理
    onSelectItem(name) {
      if (this.selectPopup === 1) {
        this.form.country = this.countryList.find((item) => item.name === name);
        // 非沙特国家无需银行与银行账号；沙特则默认选第一家银行
        if (this.form.country.code !== "SA") {
          this.form.bank = "";
          this.form.bankAccount = "";
        } else if (!this.form.bank) {
          this.form.bank = this.bankList[0];
        }
      } else if (this.selectPopup === 2) {
        this.form.bank = name;
      }
      this.closeSelect();
    },
    // 确认绑定：校验表单后通知父组件
    confirmBind() {
      const result = validateSettlementForm(this.form, this.$t.bind(this));
      if (!result.valid) {
        Toast(result.message);
        return;
      }
      this.$emit("input", { ...this.form });
      Toast(this.$t("transfer.bindSuccess"));
      this.$emit("bound", { ...this.form });
    },
  },
};
</script>

<style scoped>
/* —— 底部滑出/滑入过渡动画 —— */
/* 进入与离开都应用过渡：遮罩淡入淡出，面板从底部滑动 */
.psbs-enter-active,
.psbs-leave-active {
  transition: opacity 0.25s ease;
}
/* 遮罩进入前/离开后透明 */
.psbs-enter,
.psbs-leave-to {
  opacity: 0;
}

/* 面板从屏幕底部滑入 */
.psbs-enter-active .psbs-sheet,
.psbs-leave-active .psbs-sheet {
  transition: transform 0.3s ease;
}
/* 进入前/离开后下移到屏幕下方（隐藏） */
.psbs-enter .psbs-sheet,
.psbs-leave-to .psbs-sheet {
  transform: translateY(100%);
}

.psbs-mask {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
}

.psbs-sheet {
  position: relative;
  width: 100%;
  max-height: 60vh;
  background: #fff;
  border-radius: 24px 24px 0 0;
  display: flex;
  flex-direction: column;
  font-family: "Mada", sans-serif;
  overflow: hidden;
}

.psbs-header {
  position: relative;
  padding: 40px 32px 24px;
  text-align: center;
  flex-shrink: 0;
}

.psbs-title {
  margin: 0;
  font-size: 28px;
  font-weight: 500;
  color: #1f1315;
}

.psbs-close {
  position: absolute;
  right: 28px;
  top: 32px;
  width: 28px;
  height: 28px;
}

.psbs-close::before,
.psbs-close::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 2px;
  background: #121212;
}

.psbs-close::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.psbs-close::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.psbs-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scroll-padding: 16px 0 120px;
}

.psbs-body {
  padding: 0 28px calc(120px + env(safe-area-inset-bottom));
  padding-bottom: calc(120px + constant(safe-area-inset-bottom));
}

.psbs-field {
  margin-bottom: 20px;
}

.psbs-field label {
  display: block;
  margin-bottom: 8px;
  font-size: 24px;
  font-weight: 400;
  color: #89848d;
}

.psbs-field .req {
  color: #ff4365;
  font-style: normal;
  margin-left: 4px;
}

.psbs-field input,
.psbs-select {
  width: 100%;
  height: 88px;
  padding: 0 24px;
  border: 1px solid #dbdbdb;
  border-radius: 24px;
  font-size: 28px;
  box-sizing: border-box;
  background: #fff;
  line-height: 88px;
  color: #262427;
  display: block;
  font-family:'Montserrat';
}

.psbs-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.psbs-select::after {
  content: "";
  width: 28px;
  height: 28px;
  background: url("../../assets/withdrawal/topage.png") no-repeat;
  background-size: 100% 100%;
  flex-shrink: 0;
}

.psbs-tips {
  width: 100%;
  margin-top: 8px;
  padding: 20px 24px;
  border-radius: 16px;
  background: linear-gradient(
    360deg,
    rgba(255, 147, 147, 0) 0%,
    rgba(255, 147, 147, 0.25) 100%
  );
  font-size: 22px;
  line-height: 34px;
  color: #262427;
  box-sizing: border-box;
}

.psbs-tips-title {
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: 500;
  color: #ff4365;
}

.psbs-tips-title::before {
  display: inline-block;
  content: "";
  width: 32px;
  height: 32px;
  background: url("../../assets/withdrawal_v2/elert.png") no-repeat;
  background-size: 100% 100%;
  vertical-align: top;
  margin-right: 8px;
}

.psbs-tips ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.psbs-tips li {
  margin-bottom: 8px;
}

.psbs-tips li:last-child {
  margin-bottom: 0;
}

.psbs-float-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(24px + env(safe-area-inset-bottom));
  bottom: calc(24px + constant(safe-area-inset-bottom));
  display: flex;
  justify-content: center;
  padding: 0 28px;
  z-index: 3;
  pointer-events: none;
}

/* .psbs-float-bar::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 56px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
  pointer-events: none;
} */

.psbs-submit {
  pointer-events: auto;
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 412px;
  height: 80px;
  border: none;
  border-radius: 40px;
  background: #8131e7;
  font-size: 24px;
  font-weight: 500;
  color: #fff;
  box-shadow: 0 8px 24px rgba(129, 49, 231, 0.28);
}

/* —— 国家/银行选择层过渡动画 —— */
/* 遮罩淡入淡出（transition 根元素是 .psbs-select-mask 自身） */
.psbs-select-mask-enter-active,
.psbs-select-mask-leave-active {
  transition: opacity 0.25s ease;
}
.psbs-select-mask-enter,
.psbs-select-mask-leave-to {
  opacity: 0;
}

/* 选择层面板从底部滑出（transition 根元素是 .psbs-select-popup 自身） */
.psbs-select-enter-active,
.psbs-select-leave-active {
  transition: opacity 0.25s ease, transform 0.3s ease;
}
.psbs-select-enter,
.psbs-select-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.psbs-select-mask {
  position: fixed;
  inset: 0;
  z-index: 310;
  background: rgba(0, 0, 0, 0.3);
}

.psbs-select-popup {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 320;
  transform: translateY(0);
}

.hide-scrollbar {
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

html[lang="ar"] .psbs-sheet {
  direction: rtl;
}

html[lang="ar"] .psbs-close {
  right: auto;
  left: 28px;
}

html[lang="ar"] .psbs-field .req {
  margin-left: 0;
  margin-right: 4px;
}

html[lang="ar"] .psbs-tips-title::before {
  margin-right: 0;
  margin-left: 8px;
}

html[lang="ar"] .psbs-select::after {
  transform: scaleX(-1);
}
</style>
